// 004 US4 + US6 (FR-020–FR-023, FR-026, FR-049–FR-052; BR-14, BR-15): Change school (mode "move") and
// Link with school code (mode "link") for one child. Feature 003's code step finds and confirms the
// school; the same school as the child's current one sends nothing and offers Edit class and section;
// otherwise class and section come from the new school's list and the change is sent as the code,
// class, section and join source CODE (never a school id). Refusals use the profile messages.
import { useState } from "react";
import { View } from "react-native";

import { isAppError } from "@/api/errors";
import type { Api } from "@/api/types";
import { EnterSchoolCodeForm } from "@/join/EnterSchoolCodeForm";
import { useJoinClient } from "@/join/joinApi";
import { SchoolSummaryCard } from "@/join/SchoolSummaryCard";
import { appNotices } from "@/shell/notice/store";
import { useNetworkStatus } from "@/shell/useNetworkStatus";
import { Button } from "@/ui/Button";
import { InlineMessage } from "@/ui/InlineMessage";
import { Text } from "@/ui/Text";

import { ClassSectionPickers, type ClassSection } from "../ClassSectionPickers";
import { PROFILE_COPY, profileErrorMessage } from "../copy";
import { Card, OfflineNote, useProfileStyles } from "../ui";
import { useParentData } from "../useParentData";

type School = Api["SchoolByCode"];
type Step =
  | { s: "code" }
  | { s: "same"; school: School }
  | { s: "pick"; code: string; school: School };

const EMPTY: ClassSection = { classLevel: null, section: null };

export function ChangeSchoolFlow({
  student,
  mode,
  intro,
  onDone,
  onCancel,
  onEditClassSection,
}: {
  student: Api["Student"];
  mode: "move" | "link";
  /** A message shown above the code step (for example why the child needs linking). */
  intro?: string | null;
  onDone: () => void;
  onCancel: () => void;
  onEditClassSection: () => void;
}) {
  const styles = useProfileStyles();
  const { online } = useNetworkStatus();
  const join = useJoinClient();
  const { actions } = useParentData();
  const [step, setStep] = useState<Step>({ s: "code" });
  const [value, setValue] = useState<ClassSection>(EMPTY);
  const [errors, setErrors] = useState<Record<string, string | undefined>>({});
  const [formError, setFormError] = useState<string | null>(null);
  const [pending, setPending] = useState(false);

  function confirmed(code: string, school: School) {
    setValue(EMPTY);
    setErrors({});
    setFormError(null);
    if (student.school !== null && school.id === student.school.id)
      setStep({ s: "same", school });
    else setStep({ s: "pick", code, school });
  }

  async function save() {
    if (step.s !== "pick" || pending || !online) return;
    const next = {
      classLevel: value.classLevel ? undefined : PROFILE_COPY.chooseClass,
      section: value.section ? undefined : PROFILE_COPY.chooseSection,
    };
    setErrors(next);
    setFormError(null);
    if (next.classLevel || next.section) return;
    setPending(true);
    try {
      await actions.updateStudent(student.id, {
        schoolCode: step.code,
        classLevel: value.classLevel as Api["ClassLevelCode"],
        section: value.section!,
        joinedVia: "CODE",
      });
      appNotices.post(PROFILE_COPY.added(student.name, step.school.name));
      onDone();
    } catch (e) {
      if (!isAppError(e)) throw e;
      if (e.fieldErrors.some((f) => f.code === "NOT_IN_SCHOOL_CLASSES")) {
        // The new school's list changed: reload it, clear the choice and ask again (FR-044, FR-051).
        const fresh = await join
          .getSchoolByJoinCode(step.code)
          .catch(() => step.school);
        setStep({ s: "pick", code: step.code, school: fresh });
        setValue(EMPTY);
        setErrors({ classLevel: PROFILE_COPY.classGone });
      } else {
        const msg = profileErrorMessage(e, {
          action: mode,
          child: student.name,
          school: step.school.name,
        });
        // No name field here: a duplicate-name refusal (FR-054) is shown as the form message.
        setErrors({
          classLevel: msg.fields?.classLevel,
          section: msg.fields?.section,
        });
        setFormError(msg.form ?? msg.fields?.name ?? null);
      }
    } finally {
      setPending(false);
    }
  }

  if (step.s === "same") {
    return (
      <Card testID="change-school-same">
        <InlineMessage tone="warning">
          {PROFILE_COPY.sameSchool(student.name, step.school.name)}
        </InlineMessage>
        <View style={styles.buttons}>
          <Button variant="outline" onPress={() => setStep({ s: "code" })}>
            {PROFILE_COPY.changeSchool}
          </Button>
          <Button onPress={onEditClassSection}>
            {PROFILE_COPY.editClassSection}
          </Button>
        </View>
      </Card>
    );
  }

  if (step.s === "pick") {
    return (
      <Card testID="change-school-pick">
        <SchoolSummaryCard school={step.school} />
        <ClassSectionPickers
          classes={step.school.classes}
          schoolName={step.school.name}
          value={value}
          onChange={setValue}
          errors={{ classLevel: errors.classLevel, section: errors.section }}
          disabled={!online}
        />
        {formError ? (
          <InlineMessage tone="error">{formError}</InlineMessage>
        ) : null}
        <OfflineNote online={online} />
        <View style={styles.buttons}>
          <Button variant="outline" onPress={() => setStep({ s: "code" })}>
            {PROFILE_COPY.changeSchool}
          </Button>
          <Button
            onPress={() => void save()}
            pending={pending}
            disabled={!online}
          >
            {PROFILE_COPY.save}
          </Button>
        </View>
      </Card>
    );
  }

  return (
    <Card testID="change-school-code">
      {intro ? <InlineMessage tone="warning">{intro}</InlineMessage> : null}
      <Text weight="semiBold">
        {mode === "move"
          ? PROFILE_COPY.changeSchoolHeading
          : PROFILE_COPY.linkWithCode}
      </Text>
      <EnterSchoolCodeForm showHeading={false} onConfirmed={confirmed} />
      <Button variant="outline" onPress={onCancel}>
        {PROFILE_COPY.cancel}
      </Button>
    </Card>
  );
}
